import { VideoLibraryBreakpoints } from '@/constants';

const breakpointMap: Map<string, [string, number][]> = new Map();

type GetSmallestBreakpointForWidthOptions = {
  /** Object defining the breakpoints to consider */
  breakpoints: Record<string, number>;
  /** Use reverse order for breakpoints */
  reverseSort: boolean;
  /** For testing, reset the breakpoint cache */
  reset: boolean;
};

const defaultOptions: GetSmallestBreakpointForWidthOptions = {
  breakpoints: VideoLibraryBreakpoints,
  reset: false,
  reverseSort: false,
};

/**
 * Check if the value is a float
 */
function isFloat(num: number | string): boolean {
  const coercedNumber = Number(num);
  return !isNaN(coercedNumber) && coercedNumber === num && num % 1 !== 0;
}

/**
 * Return the name of the smallest breakpoint that matches the given width.
 *
 * Used to create classnames based on breakpoint size. In cases where no
 * breakpoints match, this will return null.
 */
export function getSmallestBreakpointForWidth(
  width: number,
  options: Partial<GetSmallestBreakpointForWidthOptions> = {},
): string | null {
  const callOptions = { ...defaultOptions, ...options };
  if (!(Number.isInteger(width) || isFloat(width))) {
    return null;
  }
  if (width <= 0) {
    return null;
  }

  let sortedEntries: null | [string, number][] = null;
  const mapKey = JSON.stringify({ ...callOptions.breakpoints, _reverse: callOptions.reverseSort });

  if (callOptions.reset) {
    breakpointMap.clear();
  }

  if (breakpointMap.has(mapKey)) {
    sortedEntries = breakpointMap.get(mapKey) ?? [];
  } else {
    sortedEntries = Object.entries(callOptions.breakpoints)
      .filter(([key]) => !/^_/.test(key))
      .sort((entryA, entryB) => {
        if (entryA[1] > entryB[1]) {
          return callOptions.reverseSort ? -1 : 1;
        }
        if (entryA[1] < entryB[1]) {
          return callOptions.reverseSort ? 1 : -1;
        }
        return 0;
      });
    breakpointMap.set(mapKey, sortedEntries);
  }

  for (let index = 0; index < sortedEntries.length; index++) {
    const entry = sortedEntries[index];
    if (width <= entry[1]) {
      return entry[0];
    }
  }

  return null;
}
